'use client';

import { cn } from '../lib/utils';
import React, { useEffect, useState } from 'react';

// Lazy load shiki to reduce initial bundle size
let codeToHtml: any = null;
let shikiLoadPromise: Promise<void> | null = null;

const loadShiki = async () => {
  if (shikiLoadPromise) return shikiLoadPromise;
  
  shikiLoadPromise = (async () => {
    const shikiModule = await import(/* webpackChunkName: "shiki" */ 'shiki');
    codeToHtml = shikiModule.codeToHtml;
  })();
  
  return shikiLoadPromise;
};

export type CodeBlockProps = {
  children?: React.ReactNode;
  className?: string;
} & React.HTMLProps<HTMLDivElement>;

function CodeBlock({ children, className, ...props }: CodeBlockProps) {
  return (
    <div
      className={cn(
        'not-prose flex w-full flex-col overflow-clip border',
        'border-border bg-card text-card-foreground rounded-xl',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export type CodeBlockCodeProps = {
  code: string;
  language?: string;
  theme?: string;
  className?: string;
} & React.HTMLProps<HTMLDivElement>;

function CodeBlockCode({
  code,
  language = 'tsx',
  theme = 'github-light',
  className,
  ...props
}: CodeBlockCodeProps) {
  const [highlightedHtml, setHighlightedHtml] = useState<string | null>(null);

  useEffect(() => {
    async function highlight() {
      if (!code) {
        setHighlightedHtml('<pre><code></code></pre>');
        return;
      }

      try {
        // Load shiki if not already loaded
        await loadShiki();
        
        if (codeToHtml) {
          const html = await codeToHtml(code, { lang: language, theme });
          setHighlightedHtml(html);
        } else {
          // Fallback if shiki fails to load
          setHighlightedHtml(`<pre><code class="language-${language}">${code}</code></pre>`);
        }
      } catch (error) {
        console.error('Failed to load syntax highlighter:', error);
        // Fallback to plain code
        setHighlightedHtml(`<pre><code class="language-${language}">${code}</code></pre>`);
      }
    }
    highlight();
  }, [code, language, theme]);

  const classNames = cn('w-full overflow-x-auto text-[13px] [&>pre]:px-4 [&>pre]:py-4', className);

  // SSR fallback: render plain code if not hydrated yet
  return highlightedHtml ? (
    <div className={classNames} dangerouslySetInnerHTML={{ __html: highlightedHtml }} {...props} />
  ) : (
    <div className={classNames} {...props}>
      <pre>
        <code>{code}</code>
      </pre>
    </div>
  );
}

export type CodeBlockGroupProps = React.HTMLAttributes<HTMLDivElement>;

function CodeBlockGroup({ children, className, ...props }: CodeBlockGroupProps) {
  return (
    <div className={cn('flex items-center justify-between', className)} {...props}>
      {children}
    </div>
  );
}

export { CodeBlockGroup, CodeBlockCode, CodeBlock };
